import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '@root/components/toast';
import { IPackageDetail } from '@root/types/package';
import { useSubscriptionDetailData } from './useDetail';
import { subscriptionApi } from '@root/api';
import useUnBindDevices from '../useUnBindDevices';
import { IUnbindDevice } from '@root/types/device';
import { useSubscriptionsState } from '@root/state';
import { UNLIMITED_PACKAGE_NUMBER } from '@root/constant';

const useAdjustDevice = (): {
  selected: string[];
  detail: IPackageDetail;
  loading: number;
  requesting: boolean;
  unBindDevices: IUnbindDevice[];
  onSelected: (vs: any[]) => void;
  onUpdatePackage: () => void;
  onCancel: () => void;
  getDetail: () => void;
  onRemove: () => void;
  onNewSubscribe: () => void;
  updateDeviceLockStatus: (deviceId: string, lockStatus: number) => void;
} => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { list: unBindDevices, getUnBindDevices } = useUnBindDevices();
  const { id } = useParams();
  const { detail, loading, getDetail } = useSubscriptionDetailData(id);
  const expiredSubscriptionList = useSubscriptionsState(
    (state) => state.setExpiredData,
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [requesting, setRequesting] = useState(false);

  // 更新绑定的设备
  const onUpdatePackage = useCallback(() => {
    if (!selected.length || !id) {
      return;
    }

    setRequesting(true);
    subscriptionApi
      .deviceSwitchPackage({
        deviceIds: selected,
        packageId: id,
        type: 0,
      })
      .then(() => {
        setSelected([]);
        setRequesting(false);
        // 更新详情
        getDetail();
        // 首页数据为过期数据
        expiredSubscriptionList();
        // 更新未绑定设备列表
        getUnBindDevices();
      })
      .catch(() => {
        setRequesting(false);
      });
  }, [expiredSubscriptionList, getDetail, getUnBindDevices, id, selected]);

  const onSelected = useCallback((vs: any[]) => {
    setSelected(vs);
  }, []);

  // 更新设备的 lock
  // 套餐如果有变更记录 才会走这儿
  const updateDeviceLockStatus = useCallback(
    (deviceId: string, lockStatus: number) => {
      // 添加设备到计划
      if (lockStatus === 1) {
        const devLockStatus = detail.devList?.length
          ? detail.devList.filter((i) => i.lockStatus === 1).length
          : 0;
        // 新套餐的绑定设备
        const _maxDevNum = detail.packageChangeRecord.maxDevNum;
        const isUnlimited =
          detail.packageChangeRecord.buyNum === UNLIMITED_PACKAGE_NUMBER;

        // 新套餐已达到最大绑定数量
        if (!isUnlimited && devLockStatus >= _maxDevNum) {
          Toast.info(t('service_max_tips'));
          return;
        }
      }

      Toast.loading(
        {},
        0,
        0,
        undefined,
        lockStatus ? t('locking_device') : t('remove_lock'),
      );

      subscriptionApi
        .updateDeviceLockStatus({
          deviceId,
          lockStatus,
        })
        .then(() => {
          // 更新详情
          getDetail();
          // 首页数据为过期数据
          expiredSubscriptionList();
          Toast.destroy();
          Toast.info(t('save_successful'));
        })
        .catch(() => {
          Toast.destroy();
          Toast.info(t('save_failed'));
        });
    },
    [detail, expiredSubscriptionList, getDetail, t],
  );

  const onRemove = useCallback(() => {
    navigate(`/subscription/${id}/adjustDevice/remove`);
  }, [navigate, id]);

  const onNewSubscribe = useCallback(() => {
    navigate('/defaultPackage');
  }, [navigate]);

  const onCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    selected,
    detail,
    loading,
    requesting,
    unBindDevices,
    onSelected,
    onUpdatePackage,
    onCancel,
    getDetail,
    updateDeviceLockStatus,
    onRemove,
    onNewSubscribe,
  };
};

export default useAdjustDevice;
